'use client'

import { env } from '@/lib/env'

let provisionPromise: Promise<void> | null = null

/**
 * Ensures a patient account exists in the database for the current Keycloak user.
 * Called after social login authentication — if the user has no DB record yet,
 * this creates one via the dedicated provisioning endpoint.
 *
 * Safe to call multiple times — deduplicates concurrent calls and is a no-op
 * if the account already exists.
 */
export function ensureSocialLoginProvisioned(token: string): Promise<void> {
  if (provisionPromise) return provisionPromise

  provisionPromise = doProvision(token).finally(() => {
    provisionPromise = null
  })

  return provisionPromise
}

async function doProvision(token: string): Promise<void> {
  const apiUrl = env.NEXT_PUBLIC_API_GATEWAY_URL
  const realm = env.NEXT_PUBLIC_KEYCLOAK_REALM

  if (!apiUrl || !realm) return

  try {
    const response = await fetch(`${apiUrl}/auth/social-provision`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'x-realm': realm,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok && response.status !== 409) {
      console.warn('[social-provision] Provisioning failed:', response.status)
    }
  } catch (error) {
    // Non-blocking — if provisioning fails, the user will see an auth error
    // on their next API call, which is the expected behavior for non-patient users
    console.warn('[social-provision] Provisioning request failed:', error)
  }
}
